import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { GlobalHeader } from '@ingrooves/global-header-react';
import MainContainer from './components/partials/MainContainer/MainContainer';
import Auth from './AuthMiddleware/Auth';
import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './assets/i18n/i18n';
import { Config } from './Config';
import {
  AllCommunityModule,
  CellSelectionModule,
  ClipboardModule,
  ColumnsToolPanelModule,
  LicenseManager,
  ModuleRegistry,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SideBarModule,
  StatusBarModule,
} from 'ag-grid-enterprise';
import { logger } from './utils/Logger';
import SideNavigation from './components/partials/SideNavigation/SideNavigation';
import {
  isFeatureDisabled,
  SideNav,
} from './components/common/DisabledFunctionality';
import EnvironmentInfo from './components/partials/EnvironmentInfo';
import { useSelector } from 'react-redux';
import { getAuth } from './selectors/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthResult } from '@ingrooves/identity-web';

LicenseManager.setLicenseKey(Config.AGGrid_License);

ModuleRegistry.registerModules([
  AllCommunityModule,
  ServerSideRowModelModule,
  StatusBarModule,
  SideBarModule,
  CellSelectionModule,
  ColumnsToolPanelModule,
  ServerSideRowModelApiModule,
  ClipboardModule,
]);

export const queryClient = new QueryClient();

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const addPrsRouteToDistributionMenu = (
  menuItems: any,
  origin = window.location.origin
) => {
  const updateNavMenu = (navMenu: any[]) =>
    navMenu.map((item) => {
      const label =
        typeof item?.label === 'string'
          ? item.label
          : item?.label instanceof HTMLElement
            ? item.label.textContent
            : '';
      const isDistribution =
        String(item?.id ?? '').toLowerCase() === 'distribution' ||
        label?.trim().toLowerCase() === 'distribution';

      if (!isDistribution) return item;

      const prsRoutePattern = `^${escapeRegExp(origin)}(?:/|$)`;
      const existingPatterns = item.navHighlightRegexList ?? [];

      return {
        ...item,
        navHighlightRegexList: existingPatterns.includes(prsRoutePattern)
          ? existingPatterns
          : [...existingPatterns, prsRoutePattern],
      };
    });

  if (Array.isArray(menuItems?.navMenu)) {
    return { ...menuItems, navMenu: updateNavMenu(menuItems.navMenu) };
  }

  if (Array.isArray(menuItems?.menu?.navMenu)) {
    return {
      ...menuItems,
      menu: {
        ...menuItems.menu,
        navMenu: updateNavMenu(menuItems.menu.navMenu),
      },
    };
  }

  return menuItems;
};

function App() {
  const [menuItems, setMenuItems] = useState<any>();
  const { loggedIn, userLanguage } = useSelector(getAuth);
  const [showSideNav] = useState(!isFeatureDisabled(SideNav));
  const [sideNavOpen, setSideNavOpen] = useState(true);
  const containerClass = showSideNav
    ? sideNavOpen
      ? 'app-container sideNav-open'
      : 'app-container sideNav-collapsed'
    : 'app-container';

  const onLanguageChange = React.useCallback(async (language: string) => {
    try {
      await Auth.getUpdatedLanguage(language);
      i18n.changeLanguage(language);
      window.location.reload();
    } catch (e: any) {
      logger.error(e);
    }
  }, []);

  const initializeAppAndMenu = useCallback(async () => {
    Auth.setConfig(
      Config.PrsApi__AppConfig__Auth0__Domain,
      Config.PrsApi__AppConfig__Auth0__ClientId,
      Config.PrsApi__AppConfig__Auth0__Audience,
      Config.ProjectService__AppConfig__ApiUrl,
      Config.ProjectService__AppConfig__Auth0__ClientId,
      Config.ProjectService__AppConfig__Auth0__Audience,
      Config.PRS_MENU_CONFIG_AUDIENCE,
      Config.PRS_MENU_URL,
      Config.USER_METADATA
    );

    try {
      let hasAccessToken: boolean = false;

      if (window.location.pathname === '/logout') {
        // flush auth data
        await Auth.renewClientSession();
        // logout the user from current session
        await Auth.userLogout();
      } else {
        // get base client session tokens
        await Auth.renewClientSession().then((response: AuthResult) => {
          hasAccessToken = !!response?.accessToken;
        });

        if (hasAccessToken) {
          // get/set all the proper user data to render the Menus & UI
          await Auth.refreshTokens(true);
          const menuItems = await Auth.getMenuConfigAudienceAsync(true);
          setMenuItems(addPrsRouteToDistributionMenu(menuItems));
        }
      }
    } catch (e) {
      logger.error('Something went wrong while trying to initialize PRS');
      await Auth.userLogout();
    }
  }, []);

  useEffect(() => {
    initializeAppAndMenu();
  }, [initializeAppAndMenu]);

  // Set html language on user language load so locale sensitives operations (i.e. uppercase) are correct.
  useEffect(() => {
    const defaultLang = 'en';
    const lang = userLanguage || defaultLang;
    i18n.changeLanguage(lang);
    document.documentElement.setAttribute('lang', lang);
  }, [userLanguage]);

  const toggleSideNav = () => {
    setSideNavOpen(!sideNavOpen);
  };

  return (
    <div>
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <I18nextProvider i18n={i18n}>
            {loggedIn ? (
              <>
                <GlobalHeader
                  menuItems={menuItems}
                  activeProduct="central"
                  changePasswordUrl="/changePassword"
                  logoutUrl="/logout"
                  onLanguageChange={onLanguageChange}
                ></GlobalHeader>
                <EnvironmentInfo></EnvironmentInfo>
                <div className={containerClass}>
                  <BrowserRouter>
                    {showSideNav && (
                      <SideNavigation
                        sideNavOpen={sideNavOpen}
                        toggleSideNav={toggleSideNav}
                      ></SideNavigation>
                    )}
                    <MainContainer></MainContainer>
                  </BrowserRouter>
                </div>
              </>
            ) : null}
          </I18nextProvider>
        </div>
      </QueryClientProvider>
    </div>
  );
}

export default App;
